import React from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';

const ScientificDiagram = ({ title, caption, children }: { title: string; caption?: string; children: React.ReactNode }) => (
  <figure className="my-8 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
    <div className="p-4 border-b border-gray-200">
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      {caption && <p className="text-sm text-gray-500 mt-1">{caption}</p>}
    </div>
    <div className="p-4 overflow-x-auto">{children}</div>
  </figure>
);

const WhyMattersDiagram = () => (
  <svg viewBox="0 0 300 160" className="w-full h-auto" aria-label="CEO health impact chart">
    <rect x="0" y="0" width="300" height="160" fill="#ffffff" />
    <line x1="40" y1="20" x2="40" y2="130" stroke="#333" />
    <line x1="40" y1="130" x2="280" y2="130" stroke="#333" />
    <rect x="70" y="70" width="40" height="60" fill="#e5e7eb" />
    <rect x="130" y="50" width="40" height="80" fill="#cbd5e1" />
    <rect x="190" y="30" width="40" height="100" fill="#94a3b8" />
    <text x="90" y="145" textAnchor="middle" fontSize="12">40s</text>
    <text x="150" y="145" textAnchor="middle" fontSize="12">50s</text>
    <text x="210" y="145" textAnchor="middle" fontSize="12">60s</text>
    <text x="150" y="15" textAnchor="middle" fontSize="12" fill="#333">CEO Age vs Health Concerns</text>
  </svg>
);

const ContinuumDiagram = () => (
  <svg viewBox="0 0 500 120" className="w-full h-auto" aria-label="Care continuum diagram">
    <rect x="0" y="0" width="500" height="120" fill="#ffffff" />
    <g stroke="#000" fill="#f8f9fa" strokeWidth="1">
      <rect x="20" y="40" width="80" height="40" />
      <rect x="120" y="40" width="80" height="40" />
      <rect x="220" y="40" width="80" height="40" />
      <rect x="320" y="40" width="80" height="40" />
      <rect x="420" y="40" width="60" height="40" />
    </g>
    <g fontSize="12" textAnchor="middle">
      <text x="60" y="35">Baseline</text>
      <text x="160" y="35">Phenotype</text>
      <text x="260" y="35">Synthesis</text>
      <text x="360" y="35">Coach</text>
      <text x="450" y="35">Feedback</text>
    </g>
    <g fill="none" stroke="#000" strokeWidth="1" markerEnd="url(#arrow)">
      <defs>
        <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0 0 L6 3 L0 6 Z" fill="#000" />
        </marker>
      </defs>
      <line x1="100" y1="60" x2="120" y2="60" />
      <line x1="200" y1="60" x2="220" y2="60" />
      <line x1="300" y1="60" x2="320" y2="60" />
      <line x1="400" y1="60" x2="420" y2="60" />
    </g>
  </svg>
);

const PrototypeTimeline = () => (
  <svg viewBox="0 0 500 120" className="w-full h-auto" aria-label="Two day timeline">
    <rect x="0" y="0" width="500" height="120" fill="#ffffff" />
    <line x1="50" y1="60" x2="450" y2="60" stroke="#000" />
    <circle cx="50" cy="60" r="5" fill="#000" />
    <circle cx="250" cy="60" r="5" fill="#000" />
    <circle cx="450" cy="60" r="5" fill="#000" />
    <text x="50" y="85" textAnchor="middle" fontSize="12">Day 0</text>
    <text x="250" y="85" textAnchor="middle" fontSize="12">Day 1</text>
    <text x="450" y="85" textAnchor="middle" fontSize="12">Day 2</text>
  </svg>
);

const FollowThroughCycle = () => (
  <svg viewBox="0 0 160 160" className="w-full h-auto" aria-label="Follow through cycle">
    <circle cx="80" cy="80" r="70" fill="#ffffff" stroke="#000" strokeWidth="1" />
    <path d="M80 10 A70 70 0 0 1 150 80" fill="none" stroke="#000" strokeWidth="2" markerEnd="url(#arrow)" />
    <path d="M150 80 A70 70 0 0 1 80 150" fill="none" stroke="#000" strokeWidth="2" markerEnd="url(#arrow)" />
    <path d="M80 150 A70 70 0 0 1 10 80" fill="none" stroke="#000" strokeWidth="2" markerEnd="url(#arrow)" />
    <path d="M10 80 A70 70 0 0 1 80 10" fill="none" stroke="#000" strokeWidth="2" markerEnd="url(#arrow)" />
    <defs>
      <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
        <path d="M0 0 L6 3 L0 6 Z" fill="#000" />
      </marker>
    </defs>
  </svg>
);

const KPIGraph = () => (
  <svg viewBox="0 0 300 150" className="w-full h-auto" aria-label="KPI grid">
    <rect x="0" y="0" width="300" height="150" fill="#ffffff" />
    <rect x="20" y="20" width="110" height="50" fill="#f1f5f9" stroke="#000" />
    <rect x="170" y="20" width="110" height="50" fill="#e2e8f0" stroke="#000" />
    <rect x="20" y="90" width="110" height="40" fill="#e5e7eb" stroke="#000" />
    <rect x="170" y="90" width="110" height="40" fill="#d1d5db" stroke="#000" />
    <text x="75" y="45" textAnchor="middle" fontSize="12">Clinical</text>
    <text x="225" y="45" textAnchor="middle" fontSize="12">Cognitive</text>
    <text x="75" y="115" textAnchor="middle" fontSize="12">Physiologic</text>
    <text x="225" y="115" textAnchor="middle" fontSize="12">Business</text>
  </svg>
);


const slides: SlideContent[] = [
  {
    title: 'The Future of Executive Health',
    content: (
      <ul className="list-disc pl-5 space-y-2">
        <li>From “annual physical” to a 365-day performance platform</li>
        <li>Building on AdventHealth Celebration + Lake Nona assets</li>
        <li>Anant Vinjamoori, MD – Longevity &amp; Performance Medicine</li>
        <li>Working draft – meant to provoke, refine, and operationalize</li>
      </ul>
    ),
  },
  {
    title: 'Why This Matters Right Now',
    content: (
      <>
        <ul className="list-disc pl-5 space-y-2 mb-4">
          <li>Enterprise value moves at the speed of its leaders’ cognition &amp; vitality</li>
          <li>70% of CEOs admit health concerns have limited their output in the last 12 months*</li>
          <li>Median Fortune 500 CEO age = 58 — highest in history</li>
          <li>Preventable cardiometabolic disease is still the #1 risk to succession plans</li>
          <li>Our north star: extend leaders’ health-adjusted productive life by 10+ years</li>
        </ul>
        <ScientificDiagram title="CEO Health Pressures" caption="Sample age distribution">
          <WhyMattersDiagram />
        </ScientificDiagram>
      </>
    ),
  },
  {
    title: 'Executive Health Today = “Single-Snapshot Medicine”',
    content: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Concierge day of bundled specialist visits, labs, imaging</li>
        <li>Excellent hospitality, weak longitudinal follow-through</li>
        <li>Data lives in PDFs; nobody owns the narrative</li>
        <li>Outcomes rarely tracked, interventions rarely personalized</li>
        <li>Experience optimized for convenience, not sustained performance</li>
      </ul>
    ),
  },
  {
    title: 'Pain Points We Hear from Clients',
    content: (
      <ol className="list-decimal pl-5 space-y-2">
        <li>“Great day—then radio silence.”</li>
        <li>“Who’s my quarterback?”</li>
        <li>Fragmented advice, conflicting action plans</li>
        <li>No linkage to actual leadership KPIs (energy, focus, resilience)</li>
        <li>Limited tools for true age reversal &amp; cognitive up-leveling</li>
      </ol>
    ),
  },
  {
    title: 'AdventHealth EH—A Strong Foundation to Build On',
    content: (
      <ul className="list-disc pl-5 space-y-2">
        <li>VIP setting, zero wait time, same-day results</li>
        <li>10+ physician touch-points distilled into one seamless day</li>
        <li>Preventive oncology &amp; cardiology baked in</li>
        <li>Access to nationally recognized subspecialists</li>
        <li>Net-Promoter consistently &gt; 90 — an asset we will preserve</li>
      </ul>
    ),
  },
  {
    title: 'Enter AH Longevity and Performance Center (AHLPC)',
    content: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Human performance labs, Dexa, metabolic carts, motion capture</li>
        <li>On-site strength &amp; conditioning, neuro-cognitive testing, recovery suites</li>
        <li>Staff already designing longevity programs for elite athletes &amp; entrepreneurs</li>
        <li>Natural home for “executive performance science”</li>
        <li>Physical adjacency to sports tech/start-up ecosystem = differentiation</li>
      </ul>
    ),
  },
  {
    title: 'Vision: A Seamless Continuum of Care & Performance',
    content: (
      <>
        <ol className="list-decimal pl-5 space-y-2 mb-4">
          <li>Baseline: Clinical risk detection at Celebration</li>
          <li>Deep Phenotype: Performance, musculoskeletal, cognitive testing at LNPC</li>
          <li>Synthesis: One integrated report + action plan</li>
          <li>Quarterback: LNPC physician-coach owns the longitudinal arc</li>
          <li>Feedback Loop: Continuous data, iterative interventions, measurable outcomes</li>
        </ol>
        <ScientificDiagram title="Care Continuum" caption="From baseline to feedback">
          <ContinuumDiagram />
        </ScientificDiagram>
      </>
    ),
  },
  {
    title: 'Re-Imagined Flagship Experience (Prototype)',
    content: (
      <>
        <ul className="list-disc pl-5 space-y-1 mb-4">
          <li><strong>Day 0</strong> | Digital onboarding, wearable shipment, goals inventory</li>
          <li><strong>Day 1</strong> | Celebration: full body imaging, labs, sub-specialist consults</li>
          <li><strong>Evening</strong> | AI-assisted report draft auto-populates</li>
          <li><strong>Day 2</strong> | LNPC: VO₂max, DEXA, gait, HRV, cognitive battery, recovery eval</li>
          <li><strong>PM</strong> | 90-min synthesis w/ “quarterback” + performance coach</li>
          <li><strong>Outcome</strong> | 12-month roadmap + app access within 24 hours</li>
        </ul>
        <ScientificDiagram title="Prototype Timeline">
          <PrototypeTimeline />
        </ScientificDiagram>
      </>
    ),
  },
  {
    title: 'Longitudinal Follow-Through (The Missing 364 Days)',
    content: (
      <>
        <ul className="list-disc pl-5 space-y-2 mb-4">
          <li>Quarterly virtual check-ins, on-demand chat, rapid Rx adjustments</li>
          <li>Wearable integration: Oura, CGM, Whoop → dashboarded in Epic</li>
          <li>Dedicated concierge to coordinate local labs/therapies worldwide</li>
          <li>Annual re-up at AHLPC; leap-frog goals, recalibrate protocols</li>
          <li>Data science team mines cohort insights → continuous product learning</li>
        </ul>
        <ScientificDiagram title="Continuous Cycle">
          <FollowThroughCycle />
        </ScientificDiagram>
      </>
    ),
  },
  {
    title: 'Precision Performance Toolkit (Menu)',
    content: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Neurofeedback &amp; HRV-guided breathwork for focus under pressure</li>
        <li>Peptide / HRT protocols when indicated, under endocrine oversight</li>
        <li>GLP-1s &amp; metabolic modulators for rapid body-comp reset</li>
        <li>Supplement stack individualized via multi-omics + pharmacogenomics</li>
        <li>Exercise architecture: zone-2 + sprint interval + strength periodization</li>
        <li>Recovery tech: red-light, hyperbaric, cold-water, sleep staging</li>
        <li className="italic text-sm">(All evidence-graded, safety-cleared, IRB-trackable)</li>
      </ul>
    ),
  },
  {
    title: 'Measuring What Matters (Sample KPI Set)',
    content: (
      <>
        <ul className="list-disc pl-5 space-y-2 mb-4">
          <li><strong>Clinical</strong> – LDL-p, HbA1c, inflammatory age, VO₂max</li>
          <li><strong>Cognitive</strong> – reaction time, working-memory throughput, sustained attention</li>
          <li><strong>Physiologic</strong> – HRV, sleep efficiency, body-fat %, lean mass</li>
          <li><strong>Business</strong> – self-reported productivity, absenteeism, decision fatigue index</li>
          <li><strong>Net Benefit</strong> – Δ in health-adjusted productive years (HAPY) per exec</li>
        </ul>
        <ScientificDiagram title="KPI Categories">
          <KPIGraph />
        </ScientificDiagram>
      </>
    ),
  },
  {
    title: 'Business Impact & Next Steps',
    content: (
      <ul className="list-disc pl-5 space-y-2">
        <li>4-yr ROI model projects 6–8× cost offset via reduced burnout &amp; medical claims</li>
        <li>Competitive moat: only program marrying hospital-grade diagnostics + perf-lab + continuous care</li>
        <li>Pilot Cohort: 25 executives Q1-Q2 ’25 → refine workflow, capture data</li>
        <li>Align on build-out budget, staffing matrix, brand architecture</li>
        <li>Draft MSA, compliance, marketing brief by next 30 days</li>
        <li className="mt-2">Let’s move from “great annual check-up” to “evidence-based peak performance as a service.”</li>
      </ul>
    ),
  },
];

export default function FutureExecutiveHealth() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-8 max-w-5xl">
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4">The Future of Executive Health</h1>
        <p className="text-gray-700 mb-6">Slide deck outlining a next-generation program for high-performing leaders.</p>
        <Slideshow slides={slides} />
      </main>
      <Footer />
    </div>
  );
}

